// Share Links Service
import { getBackendUrl } from '../utils/apiUrl';
import { toErrorEnvelope } from '../utils/httpErrorEnvelope';

const CREATE_FALLBACK = 'Failed to create share link';

/**
 * Turn a failed response into an Error that still carries WHY.
 *
 * Both create paths used to throw `new Error('Failed to create share link')` and
 * drop the body on the floor. Two things depended on the reason and silently did
 * not work:
 *
 *   - ShareCardLink re-opens its consent panel on `reason === 'card_consent_required'`
 *     (the 422 from ShareLinksService.createLink). With a bare Error that branch
 *     was unreachable, so a provider who had not yet agreed got a dead end
 *     instead of the panel that would have unblocked them.
 *   - The provider's own card share was answered with a 403 whose message says
 *     exactly what to do ("Provider cards can only be shared by the provider they
 *     belong to"), and the UI replaced it with a generic alert.
 *
 * `reason` rides along via extrasOf; `message` is always a string, so a caller can
 * render it without taking its route down. See utils/httpErrorEnvelope.
 */
async function failure(response) {
  const body = await response.json().catch(() => ({}));
  const env = toErrorEnvelope(response.status, body, CREATE_FALLBACK);
  const err = new Error(env.message);
  err.status = env.status;
  err.code = env.code;
  err.reason = env.reason || null;
  return err;
}

class ShareLinksService {
  constructor() {
    // TODO: Get this from env variables
    const BASE_URL = getBackendUrl();
    this.baseUrl = `${BASE_URL}/share-links`;
  }

  async createShareLink(resourceType, resourceId, options = {}) {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        resourceType,
        resourceId,
        ...options
      })
    });

    if (!response.ok) {
      throw await failure(response);
    }

    return response.json();
  }

  /**
   * Get the signed-in company's canonical link for one of its own listings.
   *
   * Keep this on the listing resource instead of the generic /share-links URL.
   * Some privacy extensions classify that generic cross-origin path as a
   * sharing/tracking request and block it in the browser, which produces a
   * network-level "Failed to fetch" even though the API is healthy.
   */
  async createOwnedListingLink(resourceId) {
    const BASE_URL = getBackendUrl();
    const response = await fetch(
      `${BASE_URL}/listings/${encodeURIComponent(resourceId)}/public-link`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    );

    if (!response.ok) {
      throw await failure(response);
    }

    return response.json();
  }

  /**
   * Create a share link without authentication (public endpoint)
   * Used for anonymous browsing share functionality.
   *
   * NOT for provider cards: POST /public/share/create refuses `crnaProfile`
   * outright (403), because that endpoint takes a caller-supplied resourceId with
   * no auth and the anonymous card sanitiser hands the ProviderProfile id out
   * with every shared card. Provider cards mint through createShareLink, which
   * the authed controller gates to the owner.
   */
  async createPublicShareLink(resourceType, resourceId, options = {}) {
    const BASE_URL = getBackendUrl();
    const response = await fetch(`${BASE_URL}/public/share/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        resourceType,
        resourceId,
        ...options
      })
    });

    if (!response.ok) {
      throw await failure(response);
    }

    return response.json();
  }

  async revokeShareLink(linkId) {
    const response = await fetch(`${this.baseUrl}/${linkId}/revoke`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to revoke share link');
    }

    return response.json();
  }

  // Generate QR code data URL
  generateQRCode(url) {
    // Using a simple QR code service - in production, consider using a library like qrcode
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  }

  // Copy text to clipboard
  async copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.setAttribute('data-clarity-mask', 'true');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      try {
        textArea.focus();
        textArea.select();
        if (!document.execCommand('copy')) {
          throw new Error('Could not copy to clipboard.');
        }
      } finally {
        textArea.remove();
      }
    }
  }
}

const shareLinksService = new ShareLinksService();
export default shareLinksService;
